import { FC, Suspense, useEffect, useState } from "react";

import { LoadingOutlined, EnvironmentOutlined, EyeOutlined, LinkOutlined } from "@ant-design/icons";
import { Button, Checkbox, Spin, Table, Tag, Typography, Dropdown, Menu } from "antd";
import {
  FilterDropdownProps,
  FilterValue,
  SortOrder,
  SorterResult,
  TablePaginationConfig,
} from "antd/es/table/interface";
import { isArray } from "lodash";
import {
  Await,
  useLoaderData,
  useNavigate,
  useRevalidator,
  useRouteLoaderData,
  useSearchParams,
} from "react-router-dom";
import { Link } from "react-router-dom";

import CollectionTableHeader from "./CollectionTableHeader";
import EditCollection from "./EditCollection";
import DateFilter from "./filters/DateFilter";
import FieldColumnFilter from "./filters/FieldColumnFilter";
import NumberFilter from "./filters/NumberFilter";
import SearchFilter from "./filters/SearchFilter";
import UsagesFilter from "./filters/UsagesFilter";
import YearFilter from "./filters/YearFilter";
import { useDisplay } from "./hooks/useDisplay";
import { useQueryParamsToState } from "./hooks/useQueryParamsToState";
import { useWindowSize } from "./hooks/useWindowSize";
import ReactDragListViewColumn from "./ReactDragListViewColumn";
import ResizableTitle from "./ResizableTitle";
import { formatAvailability } from "./layers/AvailabilityPicker";
import { Field } from "./services/collectionFields";
import { CollectionPayload, Item } from "./services/collectionItems";
import { getUserPreference, saveUserPreference, migrateUserPreferencesForMissingKeys } from "./services/userPreferences";

import "./CollectionTable.css";
import CheckboxesFilter from "./filters/CheckboxesFilter";

const dynamicFields: Record<string, Field[]> = {
  transactions_fao: [
    {
      name: "SITG",
      group: "",
      label: "SITG",
      type: "calculated",
      sort: 4,
      ui: "link",
      configuration: {
        filterable: false,
        sortable: false,
        width: 50,
      },
    },
  ],
};

const renderTableFieldColumn = (
  collectionId: string,
  field: Field,
  sort: Record<string, string | undefined>,
  filterState: [
    Record<string, string[]>,
    (filters: Record<string, string[]>) => void
  ],
  navigate: () => void,
  updateField: (fields: Field) => void,
  allFields: Field[]
) => {
  const { displayNumber } = useDisplay();
  const {
    label,
    name,
    ui,
    type,
    options,
    configuration: { filterable, sortable, width, searchSuggestion, hideInput, searchType, isLink },
  } = field;

  const [filters, setFilters] = filterState;

  // Create context menu for hiding column
  const handleHideColumn = () => {
    updateField({
      ...field,
      configuration: {
        ...field.configuration,
        hiddenColumn: true,
        userHidden: true, // Track that this was hidden by user action
      },
    });
  };

  const contextMenu = (
    <Menu>
      <Menu.Item key="hide" onClick={handleHideColumn}>
        Masquer la colonne
      </Menu.Item>
    </Menu>
  );

  const columnTitle = (
    <Dropdown overlay={contextMenu} trigger={['contextMenu']}>
      <span className="dragHandler" style={{ userSelect: 'none', cursor: 'move' }}>
        {label}
      </span>
    </Dropdown>
  );

  const filterProps = filterable
    ? {
        filteredValue: filters[name] || null,
        filtered: !!filters[name],
        filterDropdown: (props: FilterDropdownProps) => {
          return (
            <SearchFilter
              field={name}
              collectionId={collectionId}
              dropdownProps={props}
              existingFilters={filters}
              fields={allFields}
            />
          );
        },
      }
    : {};

  const headerCellProps = {
    onHeaderCell: () => ({
      width,
      onResize: (event: any) => {
        if (event.movementX) {
          const width = field.configuration.width + event.movementX;
          updateField({
            ...field,
            configuration: {
              ...field.configuration,
              width: width < 40 ? 40 : width,
            },
          });
        }
      },
    }),
  };

  let column = (
    <Table.Column
      key={name}
      title={columnTitle}
      dataIndex={name}
      width={width}
      ellipsis={true}
      render={(value, record) => {
        if (isLink && value) {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{value}</span>
              <LinkOutlined
                style={{ 
                  color: "#1890ff", 
                  cursor: "pointer", 
                  fontSize: 16,
                  flexShrink: 0
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (value) {
                    window.open(value, "_blank", "noopener,noreferrer");
                  }
                }}
              />
            </div>
          );
        }
        return value;
      }}
      sorter={sortable}
      sortOrder={sort[name] as SortOrder}
      {...filterProps}
      {...headerCellProps}
    />
  );

  if (ui === "select-dropdown") {
    column = (
      <Table.Column
        key={name}
        title={columnTitle}
        dataIndex={name}
        width={width}
        ellipsis={true}
        render={(value) => {
          const option = options?.find((option) => option.value === value);
          return value ? (
            <Tag color={option?.color || "#616161"} key={value}>
              {option?.label}
            </Tag>
          ) : (
            <div />
          );
        }}
        sorter={sortable}
        sortOrder={sort[name] as SortOrder}
        {...filterProps}
        {...headerCellProps}
      />
    );
  }

  if (ui === "boolean") {
    column = (
      <Table.Column
        key={name}
        title={columnTitle}
        dataIndex={name}
        width={width}
        ellipsis={true}
        render={(value) => {
          return <Checkbox disabled checked={!!value} />;
        }}
        sorter={sortable}
        sortOrder={sort[name] as SortOrder}
      />
    );
  }

  if (ui === "datetime") {
    const filterProps = filterable
      ? {
          filteredValue: filters[name] || null,
          filtered: !!filters[name],
          filterDropdown: (props: FilterDropdownProps) => {
            return <DateFilter dropdownProps={props} />;
          },
        }
      : {};
    column = (
      <Table.Column
        key={name}
        title={columnTitle}
        dataIndex={name}
        width={width}
        ellipsis={true}
        render={(value) => {
          try {
            return value
              ? new Intl.DateTimeFormat("fr").format(new Date(value))
              : "";
          } catch (error) {
            console.error(error);
          }
        }}
        sorter={sortable}
        sortOrder={sort[name] as SortOrder}
        {...filterProps}
        {...headerCellProps}
      />
    );
  }

  if (ui === "map") {
    column = (
      <Table.Column
        key={name}
        title={columnTitle}
        dataIndex={name}
        width={width}
        ellipsis={true}
        render={(value) => {
          return (
            value &&
            value.coordinates && (
              <Link
                target="_blank"
                to={`https://www.google.ch/maps/place/${value.coordinates[1]},${value.coordinates[0]}`}
              >
                <Button
                  size="small"
                  type="link"
                  icon={<EnvironmentOutlined />}
                ></Button>
              </Link>
            )
          );
        }}
        {...headerCellProps}
      />
    );
  }

  if (ui === "link") {
    column = (
      <Table.Column
        key={name}
        title={columnTitle}
        width={width}
        ellipsis={true}
        render={({ NoCommune, NoParcelle }) => {
          return (
            <Link
              target="_blank"
              to={`https://map.sitg.ge.ch/app/?locparcelle=${NoCommune}:${NoParcelle}&mapresources=AMENAGEMENT`}
            >
              <Button
                size="small"
                type="link"
                icon={<EnvironmentOutlined />}
              ></Button>
            </Link>
          );
        }}
        {...headerCellProps}
      />
    );
  }

  // Hide MultiFileUpload and PhotoThumbnail fields from table view
  if (name === "MultiFileUpload" || name === "PhotoThumbnail") {
    return null;
  }

  if (type === "integer" || type === "float" || type === "decimal") {
    const filterProps = filterable
      ? {
          filteredValue: filters[name] || null,
          filtered: !!filters[name],
          filterDropdown: (props: FilterDropdownProps) => {
            return (
              <NumberFilter
                field={name}
                collectionId={collectionId}
                dropdownProps={props}
                searchSuggestion={searchSuggestion}
              />
            );
          },
        }
      : {};

    column = (
      <Table.Column
        className="number-column"
        key={name}
        title={columnTitle}
        dataIndex={name}
        width={width}
        ellipsis={true}
        render={(value) =>
          (!!value || value === 0) &&
          displayNumber(`${Number(value).toFixed(type === "float" || type === "decimal" ? 2 : 0)}`)
        }
        sorter={sortable}
        sortOrder={sort[name] as SortOrder}
        {...filterProps}
        {...headerCellProps}
      />
    );
  }

  if (name === "Year") {
    const filterProps = filterable
      ? {
          filteredValue: filters[name] || null,
          filtered: !!filters[name],
          filterDropdown: (props: FilterDropdownProps) => {
            return (
              <YearFilter
                collectionId={collectionId}
                field={name}
                dropdownProps={props}
                searchSuggestion={searchSuggestion}
              />
            );
          },
        }
      : {};

    column = (
      <Table.Column
        key={name}
        title={columnTitle}
        dataIndex={name}
        width={width}
        ellipsis={true}
        sorter={sortable}
        sortOrder={sort[name] as SortOrder}
        {...filterProps}
        {...headerCellProps}
      />
    );
  }

  if (name === "AnneeConstruction") {
    const filterProps = filterable
      ? {
          filteredValue: filters[name] || null,
          filtered: !!filters[name],
          filterDropdown: (props: FilterDropdownProps) => {
            return (
              <YearFilter
                collectionId={collectionId}
                field={name}
                dropdownProps={props}
                searchSuggestion={searchSuggestion}
                min={1900}
              />
            );
          },
        }
      : {};

    column = (
      <Table.Column
        key={name}
        title={columnTitle}
        dataIndex={name}
        width={width}
        ellipsis={true}
        sorter={sortable}
        sortOrder={sort[name] as SortOrder}
        {...filterProps}
        {...headerCellProps}
      />
    );
  }

  if (name === "AllCheckboxes") {
    const checkboxOptionsByCollection: Record<string, string[]> = {
      biens_off_market: [
        "Vide (Sans valeur)",
        "Brut",
        "Ammenagé",
        "Climatisé",
      ],
    };

    const filterProps = filterable ? {
      filteredValue: filters[name] || null,
      filtered: !!filters[name],
      filterDropdown: (props: FilterDropdownProps) => {
        return <CheckboxesFilter dropdownProps={props} options={checkboxOptionsByCollection[collectionId]} />;
      },
    } : {};

    column = (
      <Table.Column
        key={name}
        title={columnTitle}
        dataIndex={name}
        width={width}
        ellipsis={true}
        sorter={sortable}
        sortOrder={sort[name] as SortOrder}
        {...filterProps}
        {...headerCellProps}
      />
    );

  }

  if (name === "Usages") {
    const filterProps = filterable
      ? {
          filteredValue: filters[name] || null,
          filtered: !!filters[name],
          filterDropdown: (props: FilterDropdownProps) => {
            return <UsagesFilter dropdownProps={props} />;
          },
        }
      : {};

    column = (
      <Table.Column
        key={name}
        title={columnTitle}
        dataIndex={name}
        width={width}
        ellipsis={true}
        sorter={sortable}
        sortOrder={sort[name] as SortOrder}
        {...filterProps}
        {...headerCellProps}
      />
    );
  }

  if (field.configuration.availabilityPicker) {
    column = (
      <Table.Column
        key={name}
        title={columnTitle}
        dataIndex={name}
        width={width}
        ellipsis={true}
        render={(value) => {
          if (!value) return "";
          if (value === "immédiate") return <Tag color="green">Immédiate</Tag>;
          return formatAvailability(value);
        }}
        sorter={sortable}
        sortOrder={sort[name] as SortOrder}
        {...filterProps}
        {...headerCellProps}
      />
    );
  }

  return (
    <Table.ColumnGroup
      key={`group-${name}`}
      title={
        filterable && (
          <FieldColumnFilter
            type={type}
            name={name}
            value={filters[name]}
            hideInput={hideInput}
            searchType={searchType}
            onFiltered={(filter, searchType) => {
              if (!filter) {
                delete filters[name];
                setFilters({ ...filters });
              } else {
                // Use different suffixes for exact vs contains search
                const suffix = searchType === "exact" ? "===" : "***";
                setFilters({ ...filters, [name]: [`${filter}${suffix}`] });
              }
              navigate();
            }}
          />
        )
      }
    >
      {column}
    </Table.ColumnGroup>
  );
};

const renderTableColumns = (
  collectionId: string,
  fields: Field[],
  sort: Record<string, string | undefined>,
  filters: [
    Record<string, string[]>,
    (filters: Record<string, string[]>) => void
  ],
  navigate: () => void,
  updateField: (field: Field) => void,
  allFields: Field[]
) => {
  return fields.filter((f)=>!f.configuration.hiddenColumn).map((field: Field) =>
    renderTableFieldColumn(
      collectionId,
      field,
      sort,
      filters,
      navigate,
      updateField,
      allFields
    )
  );
};

const CollectionTable: FC = () => {
  const revalidator = useRevalidator();
  const { width, height } = useWindowSize();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editedItemId, setEditedItemId] = useState<string | null>(
    searchParams.get("id")
  );
  const {
    filters,
    sort,
    setFilters,
    setPagination,
    setSort,
    toQueryParamsUrl,
  } = useQueryParamsToState();
  const { data, fields, limit, page, collectionId, admin } =
    useLoaderData() as CollectionPayload;
  const { user } = useRouteLoaderData("layout") as { user: any };
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [localFields, setLocalFields] = useState<Field[]>([]);

  const collectionsWithFieldPersistence = ["transactions_fao", "biens_off_market"];

  useEffect(() => {
    const loadFieldConfiguration = async () => {
      if (collectionsWithFieldPersistence.includes(collectionId) && user?.id) {
        try {
          const storedFields = await getUserPreference(user.id, collectionId, "x-fields");
          
          if (storedFields) {
            // Get current field names from Directus to validate stored fields
            const currentFieldNames = new Set(fields.map(f => f.name));
            
            // Filter out stored fields that no longer exist in Directus
            const validStoredFields = storedFields.filter((f: Field) => 
              currentFieldNames.has(f.name) || f.type === "calculated" // Keep calculated fields like SITG
            );
            
            // Migrate stored fields to include missing configuration keys (like searchType)
            const migratedFields = await migrateUserPreferencesForMissingKeys(
              user.id,
              collectionId,
              "x-fields",
              validStoredFields,
              fields
            );
            console.log(migratedFields);
            // Check for new fields from Directus that aren't in stored configuration
            const storedFieldNames = new Set(migratedFields.map((f: Field) => f.name));
            const newFields = fields.filter(field => !storedFieldNames.has(field.name));
            
            // Merge migrated stored fields with new fields at the end
            const mergedFields = [...migratedFields, ...newFields];
            
            // Add dynamic fields if they exist
            const extraFields = dynamicFields[collectionId];
            if (extraFields) {
              const filteredExtraFields = extraFields.filter(f => 
                !mergedFields.some(existingField => existingField.name === f.name)
              );
              setLocalFields([...mergedFields, ...filteredExtraFields]);
            } else {
              setLocalFields(mergedFields);
            }
          } else {
            // No stored configuration, use defaults
            const extraFields = dynamicFields[collectionId];
            if (extraFields) {
              setLocalFields([...fields, ...extraFields]);
            } else {
              setLocalFields(fields);
            }
          }
        } catch (error) {
          console.error("Error loading field configuration:", error);
          // On error, use default configuration
          const extraFields = dynamicFields[collectionId];
          if (extraFields) {
            setLocalFields([...fields, ...extraFields]);
          } else {
            setLocalFields(fields);
          }
        }
      } else {
        // For other collections or when user is not available
        const extraFields = dynamicFields[collectionId];
        if (extraFields) {
          setLocalFields([...fields, ...extraFields]);
        } else {
          setLocalFields(fields);
        }
      }
    };

    loadFieldConfiguration();
  }, [fields, collectionId, user?.id]);

  useEffect(() => {
    const saveFieldConfiguration = async () => {
      if (collectionsWithFieldPersistence.includes(collectionId) && localFields.length > 0 && user?.id) {
        try {
          await saveUserPreference(user.id, collectionId, "x-fields", localFields);
        } catch (error) {
          console.error("Error saving field configuration:", error);
        }
      }
    };

    saveFieldConfiguration();
  }, [localFields, collectionId, user?.id]);

  useEffect(() => setSelectedRowKeys([]), [collectionId]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<Item> | SorterResult<Item>[]
  ) => {
    const newFilters = Object.keys(filters).reduce((acc, key) => {
      if (!filters[key]) return acc;

      return {
        ...acc,
        [`${key.split("-")[0]}`]: filters[key],
      };
    }, {});
    setFilters(newFilters);

    const sorts = isArray(sorter) ? sorter : ([sorter] as SorterResult<Item>[]);

    setSort(
      sorts.reduce(
        (acc: Record<string, string | undefined>, { field, order }) => ({
          ...acc,
          [`${field}`]: !order ? undefined : order.toString(),
        }),
        {}
      )
    );

    setPagination(pagination.current, pagination.pageSize);

    navigate(`?${toQueryParamsUrl()}`);
  };

  const handleCloseEdit = () => {
    revalidator.revalidate();
    setEditedItemId(null);

    if (searchParams.get("id")) {
      searchParams.delete("id");
      setSearchParams(searchParams);
    }
  };

  return (
    <Suspense
      fallback={
        <div
          style={{
            width: "100%",
            height: "calc(100% - 64px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "white",
            flexDirection: "column",
            margin: "48px 0",
          }}
        >
          <Spin indicator={<LoadingOutlined style={{ fontSize: 56 }} spin />} />
          <Typography.Link style={{ marginTop: 24, fontSize: 16 }}>
            Chargement en cours...
          </Typography.Link>
        </div>
      }
    >
      <Await resolve={data}>
        {(data) => {
          const hiddenFields = localFields.filter(f => f.configuration.userHidden);
          const currentIndex = editedItemId
            ? data.items.findIndex((it: Item) => String(it.id) === editedItemId)
            : -1;
          
          const handleShowColumn = (fieldName: string) => {
            const fieldIndex = localFields.findIndex(f => f.name === fieldName);
            if (fieldIndex !== -1) {
              const newLocalFields = JSON.parse(JSON.stringify(localFields));
              newLocalFields[fieldIndex].configuration.hiddenColumn = false;
              newLocalFields[fieldIndex].configuration.userHidden = false;
              setLocalFields(newLocalFields);
            }
          };

          const hiddenColumnsMenu = hiddenFields.length > 0 ? (
            <Menu>
              {hiddenFields.map(field => (
                <Menu.Item 
                  key={field.name} 
                  onClick={() => handleShowColumn(field.name)}
                >
                  Afficher "{field.label}"
                </Menu.Item>
              ))}
            </Menu>
          ) : (
            <Menu>
              <Menu.Item key="empty" disabled>
                Aucune colonne masquée par l'utilisateur
              </Menu.Item>
            </Menu>
          );

          return (
            <>
              {hiddenFields.length > 0 && (
                <div style={{ padding: "8px 8px 0 8px" }}>
                  <Dropdown overlay={hiddenColumnsMenu} trigger={['click']}>
                    <Button size="small" icon={<EyeOutlined />}>
                      Colonnes masquées ({hiddenFields.length})
                    </Button>
                  </Dropdown>
                </div>
              )}
              <ReactDragListViewColumn
                onFieldMove={(from: number, to: number) => {
                  // Get only visible fields to match the drag component's indices
                  const visibleFields = localFields.filter((f) => !f.configuration.hiddenColumn);
                  const hiddenFields = localFields.filter((f) => f.configuration.hiddenColumn);
                  
                  // Move within visible fields
                  const newVisibleFields = [...visibleFields];
                  const field = newVisibleFields.splice(from, 1)[0];
                  newVisibleFields.splice(to, 0, field);
                  
                  // Rebuild the complete fields array maintaining sort order
                  const newLocalFields = [...newVisibleFields, ...hiddenFields].sort((a, b) => {
                    // Find the new index for visible fields
                    const aIndex = newVisibleFields.findIndex(f => f.name === a.name);
                    const bIndex = newVisibleFields.findIndex(f => f.name === b.name);
                    
                    if (aIndex !== -1 && bIndex !== -1) {
                      return aIndex - bIndex;
                    }
                    if (aIndex !== -1) return -1; // visible fields come first
                    if (bIndex !== -1) return 1;
                    return a.sort - b.sort; // maintain original sort for hidden fields
                  });
                  
                  setLocalFields(newLocalFields);
                }}
                key="draggable-column"
              >
                <Table
                  bordered
                  style={{
                    paddingLeft: 8,
                    paddingRight: 8,
                  }}
                  size="small"
                  dataSource={data.items}
                  pagination={{
                    pageSize: limit,
                    pageSizeOptions: [10, 20, 50, 100, 200],
                    total: data.nbItems,
                    current: page,
                    showTotal: (total) => (
                      <div>
                        {total} / {data.total} éléments
                      </div>
                    ),
                  }}
                  rowClassName={(record, _) => 
                    (record.qpt || record.PrixZD || record.PrixNonRepresentatif) ? "row-ignored-price": ""
                  }
                  scroll={{ x: width, y: height - 250 }}
                  onRow={(record) => {
                    return {
                      onClick: () => {
                        if (record?.id != null) {
                          setEditedItemId(String(record.id));
                        }
                      },
                    };
                  }}
                  rowSelection={{
                    selectedRowKeys,
                    onChange: (selectedRowKeys) => {
                      setSelectedRowKeys(selectedRowKeys);
                    },
                  }}
                  onChange={handleTableChange}
                  title={() => (
                    <CollectionTableHeader
                      collectionId={collectionId}
                      selectedRowKeys={selectedRowKeys}
                      admin={admin}
                    />
                  )}
                  components={{ header: { cell: ResizableTitle } }}
                >
                  {renderTableColumns(
                    collectionId,
                    localFields,
                    sort,
                    [filters, setFilters],
                    () => {
                      setPagination(1, limit);
                      navigate(`?${toQueryParamsUrl()}`);
                    },
                    (field: Field) => {
                      const fieldIndex = localFields.findIndex(
                        (f) => f.name === field.name
                      );
                      const newLocalFields = JSON.parse(
                        JSON.stringify(localFields)
                      );
                      newLocalFields[fieldIndex] = field;
                      setLocalFields(newLocalFields);
                    },
                    localFields
                  )}
                </Table>
              </ReactDragListViewColumn>

              <EditCollection
                editItem={currentIndex >= 0 ? data.items[currentIndex] : undefined}
                mode="edit"
                onCloseEdit={handleCloseEdit}
                onEditNext={
                  currentIndex >= 0 && currentIndex < data.items.length - 1
                    ? () => setEditedItemId(String(data.items[currentIndex + 1].id))
                    : undefined
                }
                onEditPrevious={
                  currentIndex > 0
                    ? () => setEditedItemId(String(data.items[currentIndex - 1].id))
                    : undefined
                }
                key="edit-collection"
              />
            </>
          );
        }}
      </Await>
    </Suspense>
  );
};

export default CollectionTable;
